import React, { useState, useEffect } from 'react';
import { Tooltip } from '../../components';
import { getCurrentUser } from '../../data/session';
import { getUsers, UserProfile } from '../../services/user-service';
import { getCourses, CourseDoc } from '../../services/course-service';
import { apiFetch } from '../../services/auth-service';

const roleDot: Record<string, string> = {
  founder:  'bg-role-founder',
  mentor:   'bg-role-mentor',
  admin:    'bg-role-admin',
  investor: 'bg-role-investor',
};

const roleBadge: Record<string, string> = {
  founder:  'bg-role-founder/10 text-role-founder',
  mentor:   'bg-role-mentor/10 text-role-mentor',
  admin:    'bg-role-admin/10 text-role-admin',
  investor: 'bg-role-investor/10 text-role-investor',
};


const SectionLabel = ({ label, count }: { label: string; count?: number }) => (
  <div className="flex items-center gap-2.5 mb-4">
    <h2 className="font-display text-2xl tracking-wide text-text uppercase">{label}</h2>
    {count !== undefined && (
      <span className="text-xs bg-surface border border-line text-muted px-2 py-0.5 rounded-full font-medium">{count}</span>
    )}
  </div>
);

const AdminDashboard: React.FC = () => {
  const user = getCurrentUser();
  const firstName = user.name?.split(' ')[0] || 'Admin';

  const [users, setUsers] = useState<UserProfile[]>([]);
  const [courses, setCourses] = useState<CourseDoc[]>([]);
  const [phasePipeline, setPhasePipeline] = useState<{ phaseId: string; title: string; order: number; founderCount: number }[]>([]);
  const [phase0Count, setPhase0Count] = useState<number | null>(null);
  const [finalPitchCount, setFinalPitchCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      getUsers(),
      getCourses(),
      apiFetch.get<{ pipeline: { phaseId: string; title: string; order: number; founderCount: number }[] }>('/phases/pipeline-stats'),
      apiFetch.get<{ ideas: any[] }>('/ideas').catch(() => ({ ideas: [] })),
      apiFetch.get<{ pitches: any[] }>('/pitch/review/final/all').catch(() => ({ pitches: [] })),
    ])
      .then(([u, c, stats, ideasRes, finalRes]) => {
        setUsers(u);
        setCourses(c);
        setPhasePipeline(stats.pipeline || []);
        setPhase0Count((ideasRes.ideas || []).filter((i: any) => i.status !== 'draft').length);
        setFinalPitchCount((finalRes.pitches || []).length);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const founders  = users.filter(u => u.role === 'founder').length;
  const mentors   = users.filter(u => u.role === 'mentor').length;
  const investors = users.filter(u => u.role === 'investor').length;

  const recentUsers = [...users]
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, 8);


  return (
    <div className="space-y-10 pb-10">

      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 pt-2">
        <div>
          <p className="font-display text-xs tracking-display text-chart uppercase mb-1">Admin Panel</p>
          <h1 className="font-display text-5xl tracking-wide text-text leading-none">
            Hello, {firstName}.
          </h1>
          <p className="font-serif-display italic text-text text-xl mt-2">
            Here's your platform at a glance.
          </p>
        </div>

        {/* Stat pills */}
        <div className="flex flex-wrap gap-2 sm:pb-1">
          <Tooltip content="All registered users on the platform">
            <span className="flex items-center gap-1.5 bg-shell border border-line rounded-full px-3 py-1.5 text-xs cursor-default">
              <span className="font-bold text-text">{loading ? '–' : users.length}</span>
              <span className="text-muted">Total Users</span>
            </span>
          </Tooltip>
          <Tooltip content="Active founders in the program">
            <span className="flex items-center gap-1.5 bg-shell border border-line rounded-full px-3 py-1.5 text-xs cursor-default">
              <span className="font-bold text-chart">{loading ? '–' : founders}</span>
              <span className="text-muted">Founders</span>
            </span>
          </Tooltip>
          <Tooltip content="Mentors currently assigned to the program">
            <span className="flex items-center gap-1.5 bg-shell border border-line rounded-full px-3 py-1.5 text-xs cursor-default">
              <span className="font-bold text-role-mentor">{loading ? '–' : mentors}</span>
              <span className="text-muted">Mentors</span>
            </span>
          </Tooltip>
          <Tooltip content="Investors with platform access">
            <span className="flex items-center gap-1.5 bg-shell border border-line rounded-full px-3 py-1.5 text-xs cursor-default">
              <span className="font-bold text-role-investor">{loading ? '–' : investors}</span>
              <span className="text-muted">Investors</span>
            </span>
          </Tooltip>
          <Tooltip content="Total courses in the LMS">
            <span className="flex items-center gap-1.5 bg-shell border border-line rounded-full px-3 py-1.5 text-xs cursor-default">
              <span className="font-bold text-role-admin">{loading ? '–' : courses.length}</span>
              <span className="text-muted">Courses</span>
            </span>
          </Tooltip>
        </div>
      </div>

      {/* ── Phase Pipeline ── */}
      <section>
        <SectionLabel label="Phase Pipeline" count={phasePipeline.length + 2} />
        {loading ? (
          <p className="font-serif-display italic text-muted">Loading pipeline…</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
            {/* Phase 0 */}
            <Tooltip content="Founders who submitted their initial idea" position="bottom" className="w-full">
              <div className="w-full bg-chart/5 border border-chart/20 rounded-card p-4 text-center cursor-default animate-fade-in-up h-24 flex flex-col items-center justify-center"
                style={{ animationFillMode: 'both' }}>
                <div className="font-display text-3xl tracking-wide text-chart leading-none mb-2">
                  {phase0Count ?? '–'}
                </div>
                <div className="text-xs text-muted leading-tight">Phase 0</div>
                <div className="text-[10px] text-chart/60 leading-tight mt-0.5">Initial Idea</div>
              </div>
            </Tooltip>

            {/* Core phases */}
            {phasePipeline.map((phase, i) => (
              <Tooltip key={phase.phaseId} content="Founders currently in this phase" position="bottom" className="w-full">
                <div
                  className="w-full bg-shell border border-line rounded-card p-4 text-center hover:border-chart/40 transition-colors cursor-default animate-fade-in-up h-24 flex flex-col items-center justify-center"
                  style={{ animationDelay: `${(i + 1) * 60}ms`, animationFillMode: 'both' }}
                >
                  <div className="font-display text-3xl tracking-wide text-chart leading-none mb-2">
                    {phase.founderCount}
                  </div>
                  <div className="text-xs text-muted leading-tight">{phase.title}</div>
                </div>
              </Tooltip>
            ))}

            {/* N+1 Final Pitch */}
            <Tooltip content="Founders who submitted their final pitch for review" position="bottom" className="w-full">
              <div className="w-full bg-chart/5 border border-chart/20 rounded-card p-4 text-center cursor-default animate-fade-in-up h-24 flex flex-col items-center justify-center"
                style={{ animationDelay: `${(phasePipeline.length + 1) * 60}ms`, animationFillMode: 'both' }}>
                <div className="font-display text-3xl tracking-wide text-chart leading-none mb-2">
                  {finalPitchCount ?? '–'}
                </div>
                <div className="text-xs text-muted leading-tight">Phase {phasePipeline.length + 1}</div>
                <div className="text-[10px] text-chart/60 leading-tight mt-0.5">Final Pitch</div>
              </div>
            </Tooltip>
          </div>
        )}
      </section>

      {/* ── Recent Activity ── */}
      <section>
        <SectionLabel label="Recent Activity" count={recentUsers.length} />
        {loading ? (
          <p className="font-serif-display italic text-muted">Loading activity…</p>
        ) : recentUsers.length === 0 ? (
          <p className="font-serif-display italic text-muted">No activity yet.</p>
        ) : (
          <div className="bg-shell border border-line rounded-card divide-y divide-line overflow-hidden">
            {recentUsers.map(u => (
              <div key={u.id} className="flex items-center justify-between px-5 py-3.5">
                <div className="flex items-center gap-3">
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${roleDot[u.role] || 'bg-muted'}`} />
                  <div>
                    <span className="text-sm font-medium text-text">{u.name}</span>
                    <span className={`ml-2 text-badge font-medium px-2 py-0.5 rounded-full ${roleBadge[u.role] || 'bg-surface text-muted'}`}>
                      {u.role}
                    </span>
                  </div>
                </div>
                <span className="text-xs text-muted tabular-nums flex-shrink-0 ml-4">
                  {new Date(u.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>

    </div>
  );
};

export default AdminDashboard;
